<script lang="ts">
import type { NumberFieldRootProps as InputNumberRootProps } from 'reka-ui';
import type { Colors, Variants, Sizes, Orientation, ButtonProps } from 'types';

export interface InputNumberProps extends Omit<
  InputNumberRootProps,
  'modelValue' | 'defaultValue'
> {
  /**
   * The element or component this component should render as.
   * @defaultValue
   */
  as?: string;
  /**
   * The placeholder text when the input is empty.
   */
  placeholder?: string;
  /**
   * The color of the input
   * @defaultValue 'neutral'
   */
  color?: Colors;
  /**
   * The variant of the input.
   * Excluded `link` and `ghost` variants.
   * @defaultValue 'soft'
   */
  variant?: Exclude<Variants, 'link' | 'ghost'>;
  /**
   * The size of the input.
   * @defaultValue 'md'
   */
  size?: Sizes;
  /**
   * The orientation of the input.
   * @defaultValue 'horizontal'
   */
  orientation?: Orientation;
  /**
   * Configure the increment button. The `color` and `size` are inherited.
   * @defaultValue { variant 'link' }
   */
  increment?: ButtonProps;
  /**
   * The icon displayed to increment the value.
   * @defaultValue 'add'
   */
  incrementIcon?: string;
  /**
   * Disable the increment button
   */
  incrementDisabled?: boolean;
  /**
   * Configure the decrement button. The `color` and `size` are inherited.
   * @defaultValue { variant 'link' }
   */
  decrement?: ButtonProps;
  /**
   * The icon displayed to decrement the value.
   * @defaultValue 'remove'
   */
  decrementIcon?: string;
  /**
   * Disable the decrement button
   */
  decrementDisabled?: boolean;
  autofocus?: boolean;
  autofocusDelay?: number;
  class?: string;
  // Make vue/no-undef-properties resolve these props (transitive types aren’t detected)
  modelValue?: number | string | null;
  defaultValue?: number | string | null;
}

export interface InputNumberEmits {
  (e: 'update:modelValue' | 'change', payload: number): void;
  (e: 'blur', payload: FocusEvent): void;
}

export interface InputNumberSlots {
  increment(props?: {}): any;
  decrement(props?: {}): any;
}
</script>

<script setup lang="ts">
import { onMounted, ref, computed, watch, toRef } from 'vue';
import {
  NumberFieldRoot,
  NumberFieldIncrement,
  NumberFieldInput,
  NumberFieldDecrement,
  useForwardPropsEmits
} from 'reka-ui';
import { reactivePick } from '@vueuse/core';
import { useFormAssociated } from 'composables/useFormAssociated';

const props = withDefaults(defineProps<InputNumberProps>(), {
  color: 'neutral',
  variant: 'soft',
  size: 'md',
  orientation: 'horizontal'
});

const emit = defineEmits<InputNumberEmits>();

// Avoid rendering hidden proxy input during unit tests (prevents snapshot diffs)
const renderFormProxy = (import.meta as any)?.env?.MODE !== 'test';

const rootProps = useForwardPropsEmits(
  {
    ...reactivePick(
      props,
      'as',
      // 'modelValue', // remove: we'll drive via v-model on currentValue
      'min',
      'max',
      'step',
      'stepSnapping',
      'formatOptions',
      'disableWheelChange',
      'invertWheelChange',
      'id',
      'name',
      'disabled',
      'required'
    ),
    defaultValue: (() => {
      const num = toNum(props.defaultValue);
      return num === null ? undefined : num;
    })()
  },
  emit
);

// Expose props as refs so linter/template see them
const disabled = toRef(props, 'disabled');
const required = toRef(props, 'required');
const min = toRef(props, 'min');
const max = toRef(props, 'max');
const step = toRef(props, 'step');
const modelValueProp = toRef(props, 'modelValue');
const defaultValueProp = toRef(props, 'defaultValue');

function toNum(v: unknown): number | null | undefined {
  if (v === '' || v == null) return null;
  const n = typeof v === 'string' ? Number(v) : (v as number);
  return Number.isFinite(n) ? n : null;
}

// Local value used for form association (works for both controlled and uncontrolled usage)
const currentValue = ref<number | null | undefined>(
  toNum(modelValueProp.value ?? defaultValueProp.value)
);

// Keep local value in sync when controlled
watch(modelValueProp, v => {
  currentValue.value = toNum(v);
});

const incrementIcon = computed(
  () => props.incrementIcon || (props.orientation === 'horizontal' ? 'add' : 'chevron-up')
);
const decrementIcon = computed(
  () => props.decrementIcon || (props.orientation === 'horizontal' ? 'remove' : 'chevron-down')
);

const inputRef = ref<InstanceType<typeof NumberFieldInput> | null>(null);

// Hidden proxy input for validity + ElementInternals form value
const proxyRef = ref<HTMLInputElement | null>(null);

// Attach ElementInternals; submit number as string, omit when empty/NaN/undefined/null
const { validate } = useFormAssociated<number | null | undefined>({
  inputRef: proxyRef,
  modelValue: currentValue,
  disabled, // use refs (not props.*)
  required, // use refs
  toFormValue: v => {
    if (v === undefined || v === null || (typeof v === 'number' && !Number.isFinite(v)))
      return null;
    return String(v);
  }
});

function onUpdate(value: number) {
  currentValue.value = value;
  emit('update:modelValue', value); // keep controlled parents in sync
  emit('change', value);
  validate();
}

function onBlur(event: FocusEvent) {
  emit('blur', event);
}

function autoFocus() {
  if (props.autofocus) {
    inputRef.value?.$el?.focus();
  }
}

onMounted(() => {
  setTimeout(() => {
    autoFocus();
  }, props.autofocusDelay);
  // Initial validity pass
  validate();
});

// Re-validate when constraints change
watch(
  () => [props.min, props.max, props.step],
  () => validate()
);

const classes = computed(() => [
  'e-input-number',
  `e-input-number-color-${props.color}`,
  `e-input-number-variant-${props.variant}`,
  `e-input-number-size-${props.size}`,
  `e-input-number-orientation-${props.orientation}`,
  props.class
]);

defineExpose({
  inputRef
});
</script>

<template>
  <div>
    <number-field-root
      v-model="currentValue"
      v-bind="rootProps"
      :class="classes"
      @update:model-value="onUpdate"
    >
      <number-field-decrement
        as-child
        :disabled="disabled || props.decrementDisabled"
        class="e-input-number-decrement"
      >
        <slot name="decrement">
          <e-button
            :icon="decrementIcon"
            :color="color"
            :size="size"
            :square="orientation === 'horizontal'"
            variant="link"
            v-bind="typeof decrement === 'object' ? decrement : undefined"
            :class="
              [
                variant === 'solid' ? 'e-input-number-force-button-color' : '',
                orientation === 'vertical' ? 'e-input-number-force-button-width' : ''
              ]
                .filter(Boolean)
                .join(' ')
            "
          />
        </slot>
      </number-field-decrement>

      <number-field-input
        v-bind="$attrs"
        ref="inputRef"
        :placeholder="props.placeholder"
        class="e-input-number-input"
        @blur="onBlur"
      />

      <number-field-increment
        as-child
        :disabled="disabled || props.incrementDisabled"
        class="e-input-number-increment"
      >
        <slot name="increment">
          <e-button
            :icon="incrementIcon"
            :color="color"
            :size="size"
            :square="orientation === 'horizontal'"
            variant="link"
            v-bind="typeof increment === 'object' ? increment : undefined"
            :class="
              [
                variant === 'solid' ? 'e-input-number-force-button-color' : '',
                orientation === 'vertical' ? 'e-input-number-force-button-width' : ''
              ]
                .filter(Boolean)
                .join(' ')
            "
          />
        </slot>
      </number-field-increment>
    </number-field-root>

    <input
      v-if="renderFormProxy"
      ref="proxyRef"
      type="number"
      :required="required"
      :disabled="disabled"
      :min="min ?? undefined"
      :max="max ?? undefined"
      :step="step ?? undefined"
      :value="currentValue ?? ''"
      aria-hidden="true"
      tabindex="-1"
      class="e-input-number-proxy"
      style="
        position: absolute;
        left: -9999px;
        width: 0;
        height: 0;
        opacity: 0;
        pointer-events: none;
      "
    />
  </div>
</template>
